import { BridgeComponent } from '@hotwired/hotwire-native-bridge'
import { submitForm } from '../../utils/bridge_form'
import { withIconSvg } from '../../utils/bridge_icons'

// Bridge component rendering a native button in the iOS nav bar.
//
// Rails side: a hidden div with `data-controller="bridge--button"` carries the key, the title
// and the target URL. Web users keep the regular link behaviour.
//
// `key` (required): what the button is on its page (`edit`, `follow`…). The native side
// replaces a button by it between renders; keyed by URL, the edit pencil came back twice once
// a renamed list changed slug. Buttons taking turns (« Suivre » / « Suivi ») share a key.
// Only for text buttons: iOS keeps the icon of an icon button already in place, so icon
// buttons taking turns with different icons need distinct keys.
//
// `icon` (optional) is an SF Symbol name: when set, iOS renders an icon button and `title`
// becomes its VoiceOver label. Without it, the button stays a text CTA.
//
// Flow:
// 1. connect() sends { title, url, icon } to iOS (plus `svg`, the icon's drawing, on
//    Android: utils/bridge_icons).
// 2. The user taps the native button → iOS calls reply().
// 3. The JS callback receives the message and triggers Turbo.visit().
// `separated` (optional, default false): render this button in its own glass capsule
// instead of sharing one with the adjacent nav bar buttons (iOS 26 groups adjacent
// items by default). Use it when the action is unrelated to its neighbors.
//
// `method` (optional, default "get"): non-GET methods are submitted through an
// injected form (same recipe as bridge--menu) instead of a Turbo visit.
//
// `confirm` / `confirmTitle` / `confirmLabel` / `cancelLabel` / `destructive` (optional):
// native alert before the action fires — same contract as bridge--menu items.
// `confirmLabel` / `cancelLabel` word the dialog's two buttons (Rails i18n, see
// `bridge_confirm_labels`); without them the app falls back to its own defaults.
//
// `share` (optional, default false): the tap opens the system share sheet instead of visiting
// `url`. The controller dispatches `bridge--button:share`, for a `bridge--share` controller on the
// same element to pick up (`data-action="bridge--button:share->bridge--share#share"`): both
// components ship in every version of the apps.
//
// `haptic` (optional): native feedback on a non-GET action, carried by the injected form
// for bridge--haptic to pick up (see its controller for the available types). Opt-in per
// button: a destructive action has no reason to buzz like a confirmation.
export default class extends BridgeComponent {
  static component = 'button'
  static values = { key: String, title: String, url: String, icon: String, separated: Boolean,
                    method: String, confirm: String, confirmTitle: String, confirmLabel: String,
                    cancelLabel: String, destructive: Boolean, haptic: String, share: Boolean }

  connect() {
    super.connect()
    this.pagePath = window.location.pathname
    this.#sendConnect()
    // Rendu document caché (remise à zéro d'un onglet en arrière-plan…) : le
    // connect est ignoré par le bridge (destination inactive) et le bouton natif
    // resterait câblé sur la page morte. On rejoue au native:appeared du
    // réaffichage — UNIQUEMENT dans ce cas : un double connect systématique
    // ouvrirait des courses de doublons dans la nav bar.
    // Android : la WebView déclare « visible » même un onglet masqué (pas de
    // lien Page Visibility ↔ View), le proxy iOS ne détecte rien — on arme
    // systématiquement, l'app émet native:appeared à la sélection d'onglet et
    // son composant remplace par clé (idempotent).
    if (document.visibilityState === 'hidden' || /Listy Android/.test(navigator.userAgent)) {
      // Only for the page this controller belongs to (see menu_controller).
      this.appearedHandler = () => { if (window.location.pathname === this.pagePath) this.#sendConnect() }
      window.addEventListener('native:appeared', this.appearedHandler, { once: true })
    }
  }

  disconnect() {
    window.removeEventListener('native:appeared', this.appearedHandler)
    super.disconnect()
  }

  #sendConnect() {
    if (!this.keyValue) console.warn('bridge--button: missing key, falling back to the URL', this.urlValue)
    this.send('connect', withIconSvg({
      key: this.keyValue || this.urlValue,
      title: this.titleValue,
      url: this.urlValue,
      icon: this.iconValue,
      separated: this.separatedValue,
      confirm: this.confirmValue,
      confirmTitle: this.confirmTitleValue,
      confirmLabel: this.confirmLabelValue,
      cancelLabel: this.cancelLabelValue,
      destructive: this.destructiveValue,
    }), () => {
      if (this.shareValue) return this.dispatch('share')

      const method = (this.methodValue || 'get').toLowerCase()
      if (method === 'get') {
        window.Turbo.visit(this.urlValue)
      } else {
        submitForm(this.urlValue, method, { haptic: this.hapticValue })
      }
    })
  }
}
